import React, { useState, useCallback } from "react";
import "./App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useParams, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { SiteContext } from "./context/site";
import { SiteHeader, SiteFooter } from "./components/site/kit";
import { BackToTop, CookieBanner } from "./components/site/extras";
import AutoSeo from "./components/site/Seo";
import DiagnosticDialog from "./components/site/DiagnosticDialog";
import TalkToUs, { TalkTrigger } from "./components/site/TalkToUs";
import Home from "./pages/Home";
import { useSituation } from "./lib/situationsApi";

import SituationPage from "./components/site/SituationPage";
import CaseStudy from "./pages/CaseStudy";
import BlogPost from "./pages/BlogPost";
import WhatWeSolve from "./pages/WhatWeSolve";
import HowWeWork from "./pages/HowWeWork";
import OurWork from "./pages/OurWork";
import About from "./pages/About";
import Team from "./pages/Team";
import Insights from "./pages/Insights";
import SwitchboardStats from "./pages/SwitchboardStats";
import Admin from "./pages/Admin";
import FindYourSwitchPage from "./pages/FindYourSwitchPage";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import Legal from "./pages/Legal";
import Prototype from "./pages/Prototype";
import TypeSpecimen from "./pages/TypeSpecimen";
import NotFound from "./pages/NotFound";

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SituationRoute() {
  const { slug } = useParams();
  const { loading, data } = useSituation(slug);
  if (loading) return <div className="min-h-screen bg-white" aria-busy="true" />;
  if (!data) return <Navigate to="/404" replace />;
  return <SituationPage data={data} />;
}

function Layout() {
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagContext, setDiagContext] = useState(null);
  const [talkOpen, setTalkOpen] = useState(false);
  const [talkContext, setTalkContext] = useState(null);
  const { pathname } = useLocation();
  const bare = pathname === "/prototype" || pathname === "/type-specimen" || pathname === "/admin";

  const openDiagnostic = useCallback((query, situation) => {
    setDiagContext(query ? { query, situation } : null);
    setDiagOpen(true);
  }, []);

  const openTalk = useCallback((query, situation) => {
    setTalkContext(query ? { query, situation } : null);
    setTalkOpen(true);
  }, []);

  return (
    <SiteContext.Provider value={{ openDiagnostic, openTalk }}>
      <ScrollToHash />
      <AutoSeo />
      {!bare && <SiteHeader />}
      <main>
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/what-we-solve" element={<WhatWeSolve />} />
            <Route path="/what-we-solve/:slug" element={<SituationRoute />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/our-work" element={<OurWork />} />
            <Route path="/our-work/:slug" element={<CaseStudy />} />
            <Route path="/about" element={<About />} />
            <Route path="/team" element={<Team />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<BlogPost />} />
            <Route path="/switchboard-stats" element={<SwitchboardStats />} />
            <Route path="/find-your-switch" element={<FindYourSwitchPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/privacy" element={<Navigate to="/legal#privacy" replace />} />
            <Route path="/terms" element={<Navigate to="/legal#terms" replace />} />
            <Route path="/cookies" element={<Navigate to="/legal#cookies" replace />} />
            <Route path="/prototype" element={<Prototype />} />
            <Route path="/type-specimen" element={<TypeSpecimen />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
      </main>
      {!bare && <SiteFooter />}
      {!bare && <TalkTrigger onOpen={() => openTalk()} />}
      {!bare && <BackToTop />}
      {!bare && <CookieBanner />}
      <DiagnosticDialog open={diagOpen} onOpenChange={setDiagOpen} context={diagContext} />
      <TalkToUs open={talkOpen} onOpenChange={setTalkOpen} context={talkContext} />
    </SiteContext.Provider>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <Layout />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
